import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import Modal from '../../../shared/components/Modal';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import './PermissionsPage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE_URL = baseEnvironment.replace(/\/$/, '');

const UI_TEXT = {
  'tr-TR': {
    pageTitle: 'Yetkiler',
    pageDescription: 'Sistem yonetimi icin yetki tanimlari.',
    newPermission: 'Yeni yetki',
    code: 'Kod',
    description: 'Aciklama',
    action: 'Aksiyon',
    loading: 'Yukleniyor...',
    noRecords: 'Henuz yetki tanimi bulunamadi.',
    noDescription: 'Aciklama yok',
    edit: 'Duzenle',
    delete: 'Sil',
    editPermissionAria: 'Yetkiyi duzenle',
    createPermissionAria: 'Yeni yetki olustur',
    editPermissionTitle: 'Yetkiyi duzenle',
    createPermissionTitle: 'Yeni yetki olustur',
    close: 'Kapat',
    save: 'Kaydet',
    update: 'Guncelle',
    cancel: 'Iptal',
    saving: 'Kaydediliyor...',
    codeRequired: 'Kod zorunludur.',
    permissionsLoadError: 'Yetkiler yuklenemedi.',
    permissionsLoadErrorDetail: 'Yetkiler yuklenirken hata olustu.',
    saveFailed: 'Islem basarisiz oldu.',
    operationError: 'Islem sirasinda hata olustu.',
    permissionCreated: 'Yetki olusturuldu.',
    permissionUpdated: 'Yetki guncellendi.',
    deleteConfirm: 'Bu yetkiyi silmek istediginizden emin misiniz?',
    deleteFailed: 'Silme islemi basarisiz oldu.',
    deleteError: 'Silme sirasinda hata olustu.',
    permissionDeleted: 'Yetki silindi.'
  },
  'en-US': {
    pageTitle: 'Permissions',
    pageDescription: 'Permission definitions for system management.',
    newPermission: 'New permission',
    code: 'Code',
    description: 'Description',
    action: 'Action',
    loading: 'Loading...',
    noRecords: 'No permission definition found yet.',
    noDescription: 'No description',
    edit: 'Edit',
    delete: 'Delete',
    editPermissionAria: 'Edit permission',
    createPermissionAria: 'Create new permission',
    editPermissionTitle: 'Edit permission',
    createPermissionTitle: 'Create new permission',
    close: 'Close',
    save: 'Save',
    update: 'Update',
    cancel: 'Cancel',
    saving: 'Saving...',
    codeRequired: 'Code is required.',
    permissionsLoadError: 'Permissions could not be loaded.',
    permissionsLoadErrorDetail: 'An error occurred while loading permissions.',
    saveFailed: 'Operation failed.',
    operationError: 'An error occurred during operation.',
    permissionCreated: 'Permission created.',
    permissionUpdated: 'Permission updated.',
    deleteConfirm: 'Are you sure you want to delete this permission?',
    deleteFailed: 'Delete operation failed.',
    deleteError: 'An error occurred during delete.',
    permissionDeleted: 'Permission deleted.'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');

const normalizeKey = (value) => String(value || '')
  .toUpperCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^A-Z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

const PERMISSION_DESCRIPTION_MAP = {
  READ_WRITE: 'Data entry and viewing',
  READONLY: 'Viewing permission',
  READ_ONLY: 'Viewing permission',
  VERI_GIRISI_GORUNTULEME: 'Data entry and viewing',
  GORUNTULEME_YETKISI: 'Viewing permission'
};

const createFormState = () => ({
  id: null,
  name: '',
  description: ''
});

function PermissionsPage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const isEnglish = localeKey === 'en-US';
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);

  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');
  const [formState, setFormState] = useState(createFormState());
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isEditMode = Boolean(formState.id);

  const buildHeaders = useCallback(() => {
    const token = sessionStorage.getItem('authToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  }, []);

  const loadPermissions = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/permissions`, { headers: buildHeaders() });
      if (!response.ok) throw new Error(t('permissionsLoadError'));
      setPermissions(await response.json());
    } catch (caught) {
      setListError(caught instanceof Error ? caught.message : t('permissionsLoadErrorDetail'));
    } finally {
      setLoading(false);
    }
  }, [buildHeaders, t]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const resetForm = useCallback(() => {
    setFormState(createFormState());
    setFormError('');
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    resetForm();
  }, [resetForm]);

  const openCreateModal = () => {
    resetForm();
    setSuccess('');
    setIsModalOpen(true);
  };

  const preparePayload = useMemo(() => () => {
    const payload = { name: formState.name.trim() };
    const description = formState.description.trim();
    if (description) payload.description = description;
    return payload;
  }, [formState.description, formState.name]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFormError('');
    setSuccess('');

    const payload = preparePayload();
    if (!payload.name) {
      setFormError(t('codeRequired'));
      setSaving(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/permissions${isEditMode ? `/${formState.id}` : ''}`, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: buildHeaders(),
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || t('saveFailed'));
      }
      setSuccess(isEditMode ? t('permissionUpdated') : t('permissionCreated'));
      closeModal();
      await loadPermissions();
    } catch (caught) {
      setFormError(caught instanceof Error ? caught.message : t('operationError'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (permission) => {
    setFormState({
      id: permission.id,
      name: permission.name || '',
      description: permission.description || ''
    });
    setSuccess('');
    setFormError('');
    setIsModalOpen(true);
  };

  const handleDelete = async (permissionId) => {
    if (!window.confirm(t('deleteConfirm'))) return;

    setSaving(true);
    setListError('');
    setFormError('');
    setSuccess('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/permissions/${permissionId}`, {
        method: 'DELETE',
        headers: buildHeaders()
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || t('deleteFailed'));
      }
      setSuccess(t('permissionDeleted'));
      if (formState.id === permissionId) closeModal();
      await loadPermissions();
    } catch (caught) {
      setListError(caught instanceof Error ? caught.message : t('deleteError'));
    } finally {
      setSaving(false);
    }
  };

  const getPermissionDescription = useCallback((permission) => {
    const rawDescription = String(permission?.description || '').trim();
    if (!isEnglish) return rawDescription || t('noDescription');

    const byCode = PERMISSION_DESCRIPTION_MAP[normalizeKey(permission?.name)];
    if (byCode) return byCode;

    const byDescription = PERMISSION_DESCRIPTION_MAP[normalizeKey(rawDescription)];
    if (byDescription) return byDescription;

    return rawDescription || t('noDescription');
  }, [isEnglish, t]);

  return (
    <div className="card permissions-card">
      <PageHeader
        title={t('pageTitle')}
        description={t('pageDescription')}
        actions={<button type="button" className="btn-secondary" onClick={openCreateModal}>{t('newPermission')}</button>}
      />
      {success && <div className="alert alert-success">{success}</div>}

      <div className="permissions-grid">
        <section className="permissions-table-panel">
          <div className="table-wrapper">
            <table className="permissions-table">
              <thead>
                <tr>
                  <th>{t('code')}</th>
                  <th>{t('description')}</th>
                  <th>{t('action')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={3} className="table-placeholder">{t('loading')}</td></tr>
                ) : listError ? (
                  <tr><td colSpan={3} className="table-placeholder">{listError}</td></tr>
                ) : permissions.length === 0 ? (
                  <tr><td colSpan={3} className="table-placeholder">{t('noRecords')}</td></tr>
                ) : permissions.map((permission) => (
                  <tr key={permission.id || permission.name}>
                    <td>{permission.name}</td>
                    <td>{getPermissionDescription(permission)}</td>
                    <td className="action-cell">
                      <button type="button" className="action-icon-btn action-icon-btn-edit" onClick={() => handleEdit(permission)} aria-label={t('edit')} title={t('edit')}>
                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                          <path d="M3 17.25V21h3.75L18.81 8.94l-3.75-3.75L3 17.25Zm17.71-10.04a1.003 1.003 0 0 0 0-1.42L18.2 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 2.01-1.66Z" />
                        </svg>
                      </button>
                      <button type="button" className="action-icon-btn action-icon-btn-delete" onClick={() => handleDelete(permission.id)} aria-label={t('delete')} title={t('delete')}>
                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                          <path d="M6 7h12l-1 14H7L6 7Zm3-3h6l1 2h4v2H4V6h4l1-2Z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal} title={isEditMode ? t('editPermissionTitle') : t('createPermissionTitle')} width="560px">
        <section>
          {formError && <div className="alert alert-error">{formError}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-field">
              <label htmlFor="name">{t('code')}</label>
              <input
                id="name"
                name="name"
                value={formState.name}
                onChange={(event) => setFormState((current) => ({ ...current, name: event.target.value }))}
                placeholder="SYSTEM_MANAGE_USERS"
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="description">{t('description')}</label>
              <textarea
                id="description"
                name="description"
                rows={3}
                value={formState.description}
                onChange={(event) => setFormState((current) => ({ ...current, description: event.target.value }))}
                placeholder={isEnglish ? 'Short description' : 'Kisa aciklama'}
              />
            </div>
            <div className="form-footer">
              <button type="submit" className="btn-primary" disabled={saving}>{saving ? t('saving') : isEditMode ? t('update') : t('save')}</button>
              <button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>{t('cancel')}</button>
            </div>
          </form>
        </section>
      </Modal>
    </div>
  );
}

export default PermissionsPage;
